<template>
  <v-dialog v-model="dialog" persistent width="870px">
    <template v-slot:activator="{ on }">
      <mf-button label="Configurar variáveis" icon="settings" fill color="#611998" outlined @click="handleDialog($event, on.click)" />
    </template>
    <v-card>
      <v-card-title class="card-header">
        <div class="card-header-title">
          <span>Configurar variáveis</span>
          <mf-icon class="close-button" color="cinza-d20" size="24" @click="closeDialog">close</mf-icon>
        </div>
        <div class="card-header-description">
          <span>A variável <b>Melhor oferta</b> é flexível e se adapta a diferentes mecânicas de oferta e canais de comunicação.</span>
          <br />
          <span> Aqui, você pode configurar cada variação de texto conforme necessário.</span>
        </div>
      </v-card-title>
      <v-card-text>
        <channel-configuration-fields :channel.sync="localChannel" :offer-type.sync="localOfferType" :field.sync="localField" />
        <best-offer-configuration-text
          ref="bestOfferTextComponent"
          :channel="localChannel"
          :offer-type="localOfferType"
          :field="localField"
          :error-repeatable-text="errorRepeatableText"
          :error-validity-text="errorValidityText"
          @change="handleChange"
        />
      </v-card-text>
      <v-card-actions>
        <mf-action-buttons
          :primary-button="{
            text: 'Aplicar e salvar para campanhas futuras',
            action: () => applyChanges(true),
            isVisible: true,
            isDisabled: isLoading,
            isLoading,
          }"
          :secondary-button="{
            text: 'Aplicar somente agora',
            action: applyOnce,
            isVisible: true,
            isDisabled: isLoading,
          }"
          :extra-button="{
            text: 'Fechar',
            action: closeDialog,
            isVisible: true,
            isDisabled: isLoading,
            isLoading,
          }"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import ChannelConfigurationFields from './ChannelConfigurationFields.vue'
import BestOfferConfigurationText from './BestOfferConfigurationText.vue'
import { deepDelete } from '@/helpers/deepDelete'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'
import { getSubvariablesTitle } from '@/modules/maria/helpers/variables'

export default {
  name: 'VariablesInspection',
  components: {
    ChannelConfigurationFields,
    BestOfferConfigurationText,
  },
  mixins: [charCount, bestOfferValidations],
  props: {
    channel: {
      type: String,
      default: 'sms',
      validator: (value) => ['sms', 'push', 'whatsapp', 'email'].includes(value),
    },
    offerType: {
      type: String,
      default: 'offer_sales',
      validator: (value) =>
        [
          'offer_sales',
          'percentage_offer_sales',
          'cashback_absolute_offer_sales',
          'cashback_percentage_offer_sales',
          'takepay_offer_sales',
          'points_offer_sales',
          'combo_offer_sales',
        ].includes(value),
    },
  },
  data: () => ({
    dialog: false,
    localOfferType: 'offer_sales',
    localChannel: 'sms',
    localField: 'text',
    isLoading: false,
    errorRepeatableText: [],
    errorValidityText: [],
  }),
  computed: {
    ...mapGetters(['campaign', 'campaignVariablesConfiguration']),
    configurationId() {
      return (
        this.campaign?.messages_dispatcher?.settings?.campaign_variable_configurations?.[0]?._id ??
        this.campaign?.messages_dispatcher?.settings?.campaign_variable_configurations[0]
      )
    },
  },
  watch: {
    dialog(isOpening) {
      if (isOpening) {
        this.localChannel = this.channel
        this.localOfferType = this.offerType
        this.localField = 'text'
        this.clearErrors()
      }
    },
  },
  methods: {
    ...mapActions(['getCampaignVariablesConfiguration', 'upsertCampaignVariablesConfiguration']),

    // Método para traduzir códigos técnicos para nomes amigáveis
    getFriendlyName(channel, field, offerType) {
      const channelNames = {
        sms: 'SMS',
        push: 'Push',
        whatsapp: 'WhatsApp',
        email: 'E-mail',
      }

      const fieldNames = {
        text: 'Texto',
        title: 'Título',
        inner_message_title: 'Título interno',
        inner_message_text: 'Texto interno',
      }

      const offerTypeNames = {
        offer_sales: 'Desconto absoluto',
        percentage_offer_sales: 'Desconto percentual',
        cashback_absolute_offer_sales: 'Cashback absoluto',
        cashback_percentage_offer_sales: 'Cashback percentual',
        takepay_offer_sales: 'Leve e pague',
        points_offer_sales: 'Pontos',
        combo_offer_sales: 'Combo',
      }

      const channelName = channelNames[channel] || channel
      const fieldName = fieldNames[field] || field
      const offerTypeName = offerTypeNames[offerType] || offerType

      return `${channelName} - ${fieldName} - ${offerTypeName}`
    },

    extractSubvariables(text) {
      if (!text) return []

      const subvariables = []

      const regex = /\{[^}]*\}/g
      let match
      while ((match = regex.exec(text)) !== null) {
        const extractedText = match[0]

        const validSubvariables = getSubvariablesTitle()
        if (!validSubvariables.includes(extractedText)) {
          subvariables.push(extractedText)
        }
      }

      const openBraceRegex = /\{[^}]*$/g
      while ((match = openBraceRegex.exec(text)) !== null) {
        subvariables.push(match[0])
      }

      return subvariables
    },
    validateSubvariables(text, errorArray) {
      if (!text) return

      const validSubvariables = getSubvariablesTitle()
      const usedSubvariables = this.extractSubvariables(text)

      const invalidSubvariables = usedSubvariables.filter((subvariable) => {
        if (!subvariable.endsWith('}')) {
          return true
        }

        if (!subvariable.startsWith('{')) {
          return true
        }

        return !validSubvariables.includes(subvariable)
      })

      invalidSubvariables.forEach((subvariable) => {
        errorArray.push(`Subvariável "${subvariable}" inválida.`)
      })
    },
    async applyChanges(upsertDefault = true) {
      const bestOfferTextComponent = this.$refs.bestOfferTextComponent
      const { localMessage } = bestOfferTextComponent

      if (!this.validInput(localMessage)) {
        this.$snackbar({ message: 'Corrija os erros do formulário.', snackbarColor: 'error' })
        return
      }

      const variables = {
        _id: this.configurationId ?? null,
        upsert_default: upsertDefault,
        variable_name: '[melhor oferta]',
        variable_type: 'BEST_OFFER',
        channels: localMessage,
      }

      deepDelete(variables, '__typename')

      this.isLoading = true
      try {
        await this.upsertCampaignVariablesConfiguration(variables)

        this.$snackbar({ message: 'Configuração de variáveis salva com sucesso.', snackbarColor: 'success' })

        await this.getCampaignVariablesConfiguration({
          variableType: 'BEST_OFFER',
          id: this.configurationId,
        })

        this.countBestOfferTextChar()
        this.closeDialog()
        this.$emit('updated-variables')
      } catch (error) {
        this.$snackbar({ message: 'Erro ao salvar a configuração de variáveis.', snackbarColor: 'error' })
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    validInput(localMessage) {
      this.clearErrors()

      // Definir todas as combinações possíveis
      const channels = ['sms', 'push', 'whatsapp', 'email']
      const offerTypes = [
        'offer_sales',
        'percentage_offer_sales',
        'cashback_absolute_offer_sales',
        'cashback_percentage_offer_sales',
        'takepay_offer_sales',
        'points_offer_sales',
        'combo_offer_sales',
      ]

      // Validar todas as combinações possíveis
      channels.forEach((channel) => {
        const fields = channel === 'push' ? ['text', 'title', 'inner_message_title', 'inner_message_text'] : ['text']

        fields.forEach((field) => {
          offerTypes.forEach((offerType) => {
            const repeatableText = localMessage?.[channel]?.[field]?.[offerType]?.repeatable_text
            const validityText = localMessage?.[channel]?.[field]?.[offerType]?.validity_text

            // Validar repeatable_text (obrigatório)
            if (!repeatableText || repeatableText.trim() === '') {
              const friendlyName = this.getFriendlyName(channel, field, offerType)
              this.errorRepeatableText.push(`Campo Informações Produto não pode ser vazio para ${friendlyName}.`)
            }

            // Validar validity_text (pode estar vazio, mas se preenchido não pode ter apenas espaços)
            if (validityText && validityText.length > 0 && validityText.trim() === '') {
              const friendlyName = this.getFriendlyName(channel, field, offerType)
              this.errorValidityText.push(`Campo Data de validade não pode conter apenas espaços para ${friendlyName}.`)
            }

            // Validar subvariáveis
            this.validateSubvariables(repeatableText, this.errorRepeatableText)
            this.validateSubvariables(validityText, this.errorValidityText)

            // Validar preços
            const isFormattingInsideBestOffer = true
            if (!this.isPricesCorrect(channel, field, offerType, repeatableText, isFormattingInsideBestOffer)) {
              const friendlyName = this.getFriendlyName(channel, field, offerType)
              this.errorRepeatableText.push(
                `Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2 para ${friendlyName}.`
              )
            }

            if (!this.isPricesCorrect(channel, field, offerType, validityText, isFormattingInsideBestOffer)) {
              const friendlyName = this.getFriendlyName(channel, field, offerType)
              this.errorValidityText.push(
                `Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2 para ${friendlyName}.`
              )
            }
          })
        })
      })

      return this.errorRepeatableText.length === 0 && this.errorValidityText.length === 0
    },
    async applyOnce() {
      await this.applyChanges(false)
    },
    closeDialog() {
      this.dialog = false
      this.resetLocalMessage()
    },
    handleDialog() {
      this.dialog = true
    },
    resetLocalMessage() {
      const bestOfferTextComponent = this.$refs.bestOfferTextComponent
      bestOfferTextComponent.localMessage = JSON.parse(JSON.stringify(this.campaignVariablesConfiguration))
    },
    clearErrors() {
      this.errorRepeatableText = []
      this.errorValidityText = []
    },
    handleChange() {
      this.clearErrors()
    },
  },
}
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;

  &-title {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    span {
      font-weight: 700;
      font-size: 22px;
      line-height: 26.4px;
      letter-spacing: 1%;
      color: $cinza-d10;
    }
    .icon-wrapper {
      height: 24px;
      display: flex;
      justify-content: flex-start;
    }
  }
  &-description {
    width: 100%;
    color: $cinza-0;
    font-weight: 400;
    font-size: 16px;
    line-height: 19.2px;
    letter-spacing: 0.4px;
  }
}

.close-button {
  cursor: pointer;
}
</style>
